import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Hospital, ChargemasterMapping, HospitalStats } from "@/types/map";

interface UseHospitalMapDataOptions {
  urlState: string | null;
  urlCity: string | null;
  /** Raw city URL segment (e.g. "winston-salem") for punctuation-insensitive matching. */
  urlCitySlug?: string | null;
  hospitalId: string | null;
}

export function useHospitalMapData({ urlState, urlCity, urlCitySlug, hospitalId }: UseHospitalMapDataOptions) {
  const { toast } = useToast();
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [hospitalStats, setHospitalStats] = useState<HospitalStats>({ total: 0, pending: 0, followed: 0 });
  const [hospitalProcedures, setHospitalProcedures] = useState<ChargemasterMapping[]>([]);
  const [hospitalEarnings, setHospitalEarnings] = useState<any>(null);
  const [isLoadingEarnings, setIsLoadingEarnings] = useState(false);
  const [comparisonData, setComparisonData] = useState<ChargemasterMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [searchCode, setSearchCode] = useState("");

  // Track current selection without re-triggering the data-feed effect.
  const selectedHospitalRef = useRef<Hospital | null>(null);
  useEffect(() => { selectedHospitalRef.current = selectedHospital; }, [selectedHospital]);

  // Update data ready state when hospitals change
  useEffect(() => {
    const hasHospitalData = hospitals.length > 0;
    setIsDataReady(hasHospitalData);
  }, [hospitals, hospitalProcedures]);

  const fetchHospitalsFromDb = async (): Promise<Hospital[]> => {
    try {
      let query = supabase
        .from("hospitals")
        .select(`
          id, name, address, city, state, latitude, longitude, elevation, followed,
          website_url, transparency_portal_url, avg_markup_percentage,
          data_obfuscation_score, avg_file_size_mb, file_complexity_score,
          total_chargemaster_files, exclude_from_rankings,
          google_rating, google_review_count, cms_overall_rating,
          cms_mortality_rating, cms_readmission_rating, cms_safety_rating, cms_patient_experience_rating,
          bed_count, hospital_type, ownership, emergency_services, hospital_system,
          hospital_ranking_summary!fk_hospital_ranking_summary_hospital_id!inner(
            outlier_expensive_count, total_procedures, overall_rank_gross, avg_gross_markup_percentage,
            median_gross_markup_percentage
          )
        `)
        .eq("followed", true)
        // Server-side: only hospitals with actual procedure data (was a
        // client-side filter that fetched thousands of empty rows).
        .gt("hospital_ranking_summary.total_procedures", 0);

      if (hospitalId && !urlState) {
        // Single-segment /map/<uuid> deep link — resolve the hospital's state
        // so the map shows its surrounding market.
        const { data: targetHospital } = await supabase
          .from("hospitals")
          .select("state, city")
          .eq("id", hospitalId)
          .single();

        if (targetHospital) {
          query = query.eq("state", targetHospital.state.toUpperCase());
        }
      } else {
        if (urlState) {
          query = query.eq("state", urlState.toUpperCase());
        }
        if (urlCity && !hospitalId) {
          // Case- and punctuation-insensitive city match: try both the spaced
          // form ("Winston Salem") and the raw hyphenated slug ("winston-salem")
          // so hyphenated DB cities still match. ilike without wildcards is a
          // case-insensitive exact comparison.
          const spaced = urlCity.replace(/[,()]/g, ' ').trim();
          const hyphenated = (urlCitySlug || urlCity).replace(/[,()]/g, ' ').trim();
          query = query.or(`city.ilike."${spaced}",city.ilike."${hyphenated}"`);
        }
      }

      const { data: hospitalsData, error: hospitalsError } = await query;
      if (hospitalsError) throw hospitalsError;

      const mergedData = (hospitalsData || [])
        .filter(hospital => {
          // Exclude dedup'd duplicates / bad-chargemaster hospitals from map
          // markers — mirror /hospital-rankings. Keep a directly deep-linked
          // hospital so its URL still resolves.
          if ((hospital as any).exclude_from_rankings && hospital.id !== hospitalId) return false;
          const ranking = Array.isArray(hospital.hospital_ranking_summary)
            ? hospital.hospital_ranking_summary[0]
            : hospital.hospital_ranking_summary;
          return ranking && ranking.total_procedures > 0;
        })
        .map(hospital => {
          const ranking = Array.isArray(hospital.hospital_ranking_summary)
            ? hospital.hospital_ranking_summary[0]
            : hospital.hospital_ranking_summary;
          const outlierExpensiveCount = ranking?.outlier_expensive_count || 0;
          const totalProcedures = ranking?.total_procedures || 0;
          const outlierPercentage = totalProcedures > 0 ? (outlierExpensiveCount / totalProcedures) * 100 : 0;
          const overallRankGross = ranking?.overall_rank_gross || null;
          const avgGrossMarkupPercentage = ranking?.avg_gross_markup_percentage || 0;
          // Hospital-level headline metric (what overall_rank_gross is ordered by)
          const medianGrossMarkupPercentage = ranking?.median_gross_markup_percentage ?? null;

          const { hospital_ranking_summary, exclude_from_rankings, ...hospitalData } = hospital as any;

          return {
            ...hospitalData,
            outlier_expensive_count: outlierExpensiveCount,
            total_procedures: totalProcedures,
            outlier_percentage: outlierPercentage,
            overall_rank_gross: overallRankGross,
            avg_markup_percentage: avgGrossMarkupPercentage,
            median_markup_percentage: medianGrossMarkupPercentage
          };
        }).sort((a, b) => (b.outlier_percentage || 0) - (a.outlier_percentage || 0));

      return mergedData as Hospital[];
    } catch (error) {
      console.error("Error fetching hospitals:", error);
      toast({
        title: "Error",
        description: "Failed to fetch hospital data",
        variant: "destructive",
      });
      return [];
    }
  };

  // React-query owns the fetch. Keyed by state/city (NOT hospitalId when a
  // state is present) so a marker click within the same state doesn't refetch
  // and close the just-opened info window; staleTime keeps repeat visits warm.
  const hospitalsQuery = useQuery({
    queryKey: ['map-hospitals', urlState || (hospitalId ? `hospital:${hospitalId}` : 'USA'), urlCity || ''],
    queryFn: fetchHospitalsFromDb,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Feed the existing setState shape from the query result.
  useEffect(() => {
    const merged = hospitalsQuery.data;
    if (!merged) return;
    setHospitals(merged);

    if (hospitalId && merged.length > 0) {
      const hospitalFromUrl = merged.find(h => h.id === hospitalId);
      if (hospitalFromUrl && selectedHospitalRef.current?.id !== hospitalFromUrl.id) {
        setSelectedHospital(hospitalFromUrl);
        fetchHospitalProcedures(hospitalFromUrl.id);
        fetchHospitalEarnings(hospitalFromUrl.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalsQuery.data, hospitalId]);

  // Imperative refresh (admin edits, etc.) — forces a refetch of the active view.
  const fetchHospitals = useCallback(async () => {
    await hospitalsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalsQuery.refetch]);

  const fetchHospitalStats = async () => {
    try {
      const { count: totalCount, error: totalError } = await supabase
        .from("hospitals")
        .select("*", { count: 'exact', head: true });

      const { count: pendingCount, error: pendingError } = await supabase
        .from("hospitals")
        .select("*", { count: 'exact', head: true })
        .or("total_procedures.is.null,shame_score.is.null");

      const { count: followedCount, error: followedError } = await supabase
        .from("hospitals")
        .select("*", { count: 'exact', head: true })
        .eq("followed", true);

      if (totalError) throw totalError;
      if (pendingError) throw pendingError;
      if (followedError) throw followedError;

      setHospitalStats({
        total: totalCount || 0,
        pending: pendingCount || 0,
        followed: followedCount || 0
      });
    } catch (error) {
      console.error("Error fetching hospital stats:", error);
    }
  };

  const fetchHospitalProcedures = async (hId: string) => {
    try {
      const { count: totalCount, error: countError } = await supabase
        .from("chargemaster_mappings")
        .select("id", { count: 'exact', head: true })
        .eq("hospital_id", hId);

      if (countError) throw countError;

      const [cptCount, drgCount, icd10Count, revCount] = await Promise.all([
        supabase.from("chargemaster_mappings").select("id", { count: 'exact', head: true })
          .eq("hospital_id", hId).not("cpt_code", "is", null).neq("cpt_code", ""),
        supabase.from("chargemaster_mappings").select("id", { count: 'exact', head: true })
          .eq("hospital_id", hId).not("drg_code", "is", null).neq("drg_code", ""),
        supabase.from("chargemaster_mappings").select("id", { count: 'exact', head: true })
          .eq("hospital_id", hId).not("icd10_code", "is", null).neq("icd10_code", ""),
        supabase.from("chargemaster_mappings").select("id", { count: 'exact', head: true })
          .eq("hospital_id", hId).not("revenue_code", "is", null).neq("revenue_code", "")
      ]);

      const { data, error } = await supabase
        .from("chargemaster_mappings")
        .select(`
          id, gross_charge, discounted_cash_price, min_negotiated_rate, max_negotiated_rate,
          cpt_code, drg_code, chargemaster_description, inpatient_outpatient
        `)
        .eq("hospital_id", hId)
        .not("gross_charge", "is", null)
        .order("gross_charge", { ascending: false })
        .limit(100);

      if (error) throw error;

      // Update the selected hospital's total_procedures with the actual count
      setSelectedHospital(prev => {
        if (prev && prev.id === hId) {
          return {
            ...prev,
            total_procedures: totalCount || 0,
            cpt_count: cptCount?.count || 0,
            drg_count: drgCount?.count || 0,
            icd10_count: icd10Count?.count || 0,
            revenue_count: revCount?.count || 0
          };
        }
        return prev;
      });

      // Also update the hospitals array
      setHospitals(prevHospitals =>
        prevHospitals.map(h =>
          h.id === hId ? { ...h, total_procedures: totalCount || 0 } : h
        )
      );

      const transformedData = (data || []).map((mapping, index) => ({
        ...mapping,
        id: mapping.id || `mapping-${index}`,
        price: mapping.gross_charge || 0,
        international_avg_price: (mapping.gross_charge || 0) * 0.3,
        markup_percentage: Math.round(((mapping.gross_charge || 0) / ((mapping.gross_charge || 0) * 0.3)) * 100) - 100,
        procedure: {
          code: mapping.cpt_code || mapping.drg_code || 'N/A',
          name: mapping.chargemaster_description || 'Unknown Procedure',
          description: mapping.chargemaster_description || 'No description available'
        },
        hospital: {
          id: hId,
          name: '',
          address: '',
          city: '',
          state: '',
          latitude: 0,
          longitude: 0,
          shame_score: 0,
          outlier_expensive_count: 0,
          total_procedures: totalCount || 0,
          avg_markup_percentage: 0,
          outlier_percentage: 0
        }
      })) as ChargemasterMapping[];

      setHospitalProcedures(transformedData);
    } catch (error) {
      console.error("Error fetching hospital procedures:", error);
    }
  };

  const fetchHospitalEarnings = async (hId: string) => {
    try {
      const { data, error } = await supabase
        .from("hospital_earnings")
        .select("*")
        .eq("hospital_id", hId)
        .eq("fiscal_year", 2024)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      setHospitalEarnings(data);
    } catch (error) {
      console.error("Error fetching hospital earnings:", error);
      setHospitalEarnings(null);
    }
  };

  const searchProcedure = async () => {
    if (!searchCode.trim() || !selectedHospital) {
      toast({
        title: "Invalid Search",
        description: "Please select a hospital and enter a procedure code",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("chargemaster_mappings")
        .select(`
          id, gross_charge, discounted_cash_price, min_negotiated_rate, max_negotiated_rate,
          cpt_code, drg_code, chargemaster_description, inpatient_outpatient
        `)
        .eq("hospital_id", selectedHospital.id)
        .or(`cpt_code.ilike.%${searchCode.trim()}%,drg_code.ilike.%${searchCode.trim()}%`);

      if (error) throw error;

      const transformedData = data?.map(item => ({
        ...item,
        price: item.gross_charge || 0,
        international_avg_price: item.discounted_cash_price || item.gross_charge * 0.6 || 0,
        markup_percentage: item.gross_charge && item.discounted_cash_price
          ? ((item.gross_charge - item.discounted_cash_price) / item.discounted_cash_price) * 100
          : 0,
        procedure: {
          code: item.cpt_code || item.drg_code || 'N/A',
          name: item.chargemaster_description || 'Unknown',
          description: item.chargemaster_description || 'No description available'
        },
        hospital: selectedHospital
      })) as ChargemasterMapping[] || [];

      setComparisonData(transformedData);

      if (!data || data.length === 0) {
        toast({ title: "No Results", description: "No procedures found matching your search" });
      }
    } catch (error) {
      console.error("Error searching procedures:", error);
      toast({
        title: "Search Error",
        description: "Failed to search procedures",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load earnings when hospital changes
  useEffect(() => {
    if (selectedHospital) {
      fetchHospitalEarnings(selectedHospital.id);
    }
  }, [selectedHospital]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    hospitals,
    setHospitals,
    selectedHospital,
    setSelectedHospital,
    hospitalStats,
    hospitalProcedures,
    hospitalEarnings,
    isLoadingEarnings,
    comparisonData,
    isLoading,
    isDataReady,
    /** True once the hospitals query for the current view has completed. */
    hasFetchedHospitals: hospitalsQuery.isFetched && !hospitalsQuery.isFetching,
    searchCode,
    setSearchCode,
    fetchHospitals,
    fetchHospitalStats,
    fetchHospitalProcedures,
    fetchHospitalEarnings,
    searchProcedure,
  };
}
